<template>
  <div class='client-groups-view is-flex-col align-center p-6'>
    <b-notification v-model="error.isActive">{{ error.text }}</b-notification>
    <h1 class='title is-3 text-center'>{{ action }} Organization</h1>

    <div class='is-flex-col gap-3'>


      <section>
        <div>
          <input
            class='input is-size-4 bold'
            type='text'
            placeholder='Organization Name'
            v-model='clientGroup.name'/>
        </div>
      </section>


      <section class='card'>
        <div class='card-header p-5'>
        <h1 class='title is-4'>Members</h1>
        </div>

        <div class='card-content is-flex-col gap-1'>
          <div v-for="prospect in clientGroup.prospects" :key="prospect.email" class="prospect card p-5 is-flex justify-between" style='margin: 0'>
            <div class="is-flex gap-1-2">
              <div>
                <span
                  v-if="prospect.id === clientGroup.admin?.id"
                  :title="prospect.name + ' is the admin for ' + clientGroup.name">
                  <i class="fa fa-user has-text-primary"></i>
                </span>

                <span
                  v-else
                  class="not-admin"
                  :title="'Make ' + prospect.name + ' the admin'"
                  @click="setAdmin(prospect)">
                  <i class="fa fa-user has-text-grey-light"></i>
                </span>
              </div>

              <span>{{ prospect.name }}</span>
            </div>

            <span>{{ prospect.email }}</span>
          </div>
        </div>

        <div class="card-footer p-5 is-flex gap-1">
          <SearchProspects :select="addProspect" placeholder="Add existing member"/>

          <ProspectForm
            :callback="addProspect"
            buttonClass='is-primary is-light'>
            Create a new member
          </ProspectForm>
        </div>
        <!-- mark as admin -->
      </section>


      <section class='card'>
        <div class='card-header p-5'>
          <h1 class='title is-4'>Questionnaire</h1>
        </div>

        <div class='card-content'>
          <div>
            <button
              class='button is-primary is-light mt-1'
              @click='() => showQuestionModal = true'>
              Customize Questionnaire
            </button>
          </div>

          <b-modal v-model='showQuestionModal' can-cancel>
            <div class='card'>
              <div class='card-header'>
                <h1 class='card-header-title'>Select which questions to show in the questionnaire</h1>
              </div>

              <div class='card-content is-flex-col gap-1-2' style='max-height: 50vh; overflow-y: scroll'>
                <div>
                  <b-checkbox v-model='selectAll'>
                    <span class='bold'>Select All</span>
                  </b-checkbox>
                </div>

                <div v-for='q in allQuestions' :key='q.slug'>
                  <b-checkbox v-model='selectedQuestions'
                    :native-value='q.slug'>
                    {{ q.prompt }}
                  </b-checkbox>
                </div>
              </div>

              <div class='card-footer p-3 is-flex'>
                <button class='button is-primary' @click='updateSelectedQuestions'>Select and close</button>
              </div>
            </div>
          </b-modal>
        </div>
      </section>


      <div>
        <b-button
          class="is-primary"
          @click="createOrUpdateGroup(true)">
          {{ action === 'Edit' ? 'Save' : 'Create' }} organization
        </b-button>
      </div>

    </div>
  </div>
</template>


<script>
import ProspectForm from '@frontend/dash3/js/components/ProspectForm.vue';
import SearchProspects from '@frontend/ui/components/utils/SearchProspects.vue';
import { getQuestionnaireQuestions } from '@frontend/api/questionnaireApi';

export default {
  data() {
    return {
      error: {
        isActive: false,
        text: '',
      },
      clientGroup: {
        prospects: [],
        name: '',
        admin: {},
        questions: []
      },
      showQuestionModal: false,
      allQuestions: [],
      selectedQuestions: [],
      selectAll: false,
    };
  },
  components: {
    ProspectForm, SearchProspects,
  },
  props: {

  },
  computed: {
    clientGroupId() {
      return parseInt(this.$route.params.id);
    },
    action() {
      return this.$route.name.includes('edit') ? 'Edit' : 'Create';
    },
    allSlugs() {
      if (!this.allQuestions) return [];
      return this.allQuestions.map(q => q.slug);
    },
  },
  watch: {
    clientGroup: function(newGroup, oldGroup) {
      if (newGroup.available_questions?.length) {
        this.selectedQuestions = newGroup.available_questions;
      } else this.selectAll = true;

      if (this.allSelected()) this.selectAll = true;
    },
    allSlugs: function(newSlugs, oldSlugs) {
      if (this.allSelected()) this.selectAll = true;

      if (this.selectedQuestions?.length) return;
      this.selectAll = true;
      this.selectedQuestions = newSlugs;
    },
    selectAll: function(newState, oldState) {
      this.selectedQuestions = newState ? this.allSlugs : [];
    },
  },
  methods: {
    addProspect(data) {
      if (!data) return;
      const prospect = data.prospect ? data.prospect : data;
      this.clientGroup.prospects = [...this.clientGroup.prospects, prospect];
    },
    setAdmin(prospect) {
      this.clientGroup.admin = prospect;
      this.createOrUpdateGroup();
    },
    async createOrUpdateGroup(goHomeAfter=false) {
      if (!this.isValid()) return this.handleInvalid();

      await fetch('/api/v1/client-groups/' + this.action.toLowerCase(), {
        method: 'POST',
        body: JSON.stringify(this.clientGroup),
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': this.$root.$data.csrfToken,
        },
      });

      if (goHomeAfter) this.goHome();
    },
    isValid() {
      const group = this.clientGroup;
      return group.name && group.prospects.length;
    },
    handleInvalid() {
      const group = this.clientGroup;

      if (!group.name) {
        this.error = { text: 'Need group name', isActive: true };
        return;
      }

      if (!group.prospects.length) {
        this.error = { text: 'Need group members', isActive: true };
        return;
      }
    },
    goHome() {
      this.$router.push({ path: `/advisor/client-groups` });
    },
    allSelected() {
      return this.selectedQuestions.sort().join('') === this.allSlugs.sort().join('');
    },
    updateSelectedQuestions() {
      this.clientGroup.available_questions = this.selectedQuestions;
      this.showQuestionModal = false;
    },
  },
  async created() {
    if (this.clientGroupId) {
      const clientGroups = (await (await fetch('/api/v1/client-groups')).json()).client_groups;
      this.clientGroup = clientGroups.find(clientGroup => clientGroup.id === this.clientGroupId);
    }

    const qnr = (await getQuestionnaireQuestions('inst-qnr-v1')).data.questionnaire;

    this.allQuestions = qnr.questions.map(q => {
      return { slug: q.slug, prompt: q.prompt };
    });
  },
};
</script>


<style scoped>
.not-admin {
  opacity: 0;
  cursor: pointer;
}

.prospect:hover .not-admin {
  opacity: 1;
}
</style>
